import { Deserializable } from '../../protocols/deserializable';
import { UniquelyIdentifiable } from '../../protocols/uniquely-identifiable';
import { UnitOfMeasure } from '../../enum/dto/unit-of-measure.enum';
import { NumberUtils } from '../../../utils/number.utils';
import { LocationPriceStream } from '../../enum/shared/location-price-stream';

export const PRICING_TIER_UPPER_LIMIT = 9999;

export class VariantPricingTier implements Deserializable, UniquelyIdentifiable {
  public name: string;
  public startWeight: number; // Lower bounded weight for pricing tier
  public startQuantity: number; // Quantity of BaseVariant (StartWeight/VariantDTO.UnitSize)
  public endWeight: number; // Upper bounded weight for pricing tier
  public endQuantity: number; // Quantity of BaseVariant (StartWeight/VariantDTO.UnitSize)
  public price: number; // Indicates price per unit/uom
  public taxesInPrice: number;
  public isDiscountable: boolean;

  onDeserialize() {}

  /**
   * This functionality directly matches what is implemented on the API.
   * The selected values are validated on the API, so it is important that
   * this is only changed in conjunction with the API
   */
  getGridColumnName(useWeight: boolean, uom: UnitOfMeasure): string {
    if (useWeight && (this.startWeight > 0 || this.endWeight > 0)) {
      // Return column name using Weight
      if (this.endWeight === PRICING_TIER_UPPER_LIMIT) {
        return `${NumberUtils.formatToSigFigDecimals(this.startWeight)}${uom}+`;
      } else {
        const startWeight = NumberUtils.formatToSigFigDecimals(this.startWeight);
        const endWeight = NumberUtils.formatToSigFigDecimals(this.endWeight);
        return `${startWeight}-${endWeight}${uom}`;
      }
    } else {
      // Default to using quantity
      if (this.endQuantity === PRICING_TIER_UPPER_LIMIT) {
        return `${NumberUtils.formatToSigFigDecimals(this.startQuantity)}+`;
      } else {
        const startQty = NumberUtils.formatToSigFigDecimals(this.startQuantity);
        const endQty = NumberUtils.formatToSigFigDecimals(this.endQuantity);
        return `${startQty}-${endQty}`;
      }
    }
  }

  /**
   * Support flower sizes: 1g, 3.5g (1/8 oz), 7g (1/4 oz), 14g (1/2 oz), and 28g (1 oz).
   */
  getFlowerGridOunceColumnNames(useWeight: boolean): string[] {
    if (!useWeight) {
      return null;
    }
    // Try lower bound inclusive first (<=, >)
    let columnNames = this.getColumnNamesWithBoundStrategy(true);
    // If no results, try upper bound inclusive (<, >=)
    if (columnNames.length === 0) {
      columnNames = this.getColumnNamesWithBoundStrategy(false);
    }
    return columnNames;
  }

  private getColumnNamesWithBoundStrategy(isLowerBoundStrategy: boolean): string[] {
    const columnNames: string[] = [];
    if (this.capturesClassicSize(1, isLowerBoundStrategy)) {
      columnNames.push('1 g');
    }
    if (this.capturesClassicSize(3.5, isLowerBoundStrategy)) {
      columnNames.push('3.5 g');
    }
    if (this.capturesClassicSize(7, isLowerBoundStrategy)) {
      columnNames.push('7 g');
    }
    if (this.capturesClassicSize(14, isLowerBoundStrategy)) {
      columnNames.push('14 g');
    }
    if (this.capturesClassicSize(28, isLowerBoundStrategy)) {
      columnNames.push('28 g');
    }
    return columnNames;
  }

  private capturesClassicSize(size: number, isLowerBoundStrategy: boolean): boolean {
    // Exact match case: if start and end are both equal to the size
    if (this.startWeight === size && this.endWeight === size) {
      return true;
    }
    if (isLowerBoundStrategy) {
      // Lower bound inclusive: startWeight <= size && endWeight > size
      return this.startWeight <= size && this.endWeight > size;
    } else {
      // Upper bound inclusive: startWeight < size && endWeight >= size
      return this.startWeight < size && this.endWeight >= size;
    }
  }

  getRegularPrice(): number {
    return this.price;
  }

  getTaxesInPrice(): number {
    return this.taxesInPrice;
  }
  classicFlowerPricing(columnTitle: string, priceStream: LocationPriceStream): number | null {
    if (!this.price) return null;
    const calculateOuncesPrice = (grams: number) => {
      switch (priceStream) {
        case LocationPriceStream.TaxesIn:
        case LocationPriceStream.TaxesInRounded:
          return this.taxesInPrice * grams;
        default:
          return this.price * grams;
      }
    };
    // Determine which strategy to use (try lower bound inclusive first)
    // If the first strategy returns any results, we want to use that for getFlowerGridOunceColumnName consistency
    const useLowerBoundStrategy = this.hasAnyMatchWithStrategy(true);
    switch (true) {
      case columnTitle === '1 g' && this.capturesClassicSize(1, useLowerBoundStrategy):
        return calculateOuncesPrice(1);
      case columnTitle === '3.5 g' && this.capturesClassicSize(3.5, useLowerBoundStrategy):
        return calculateOuncesPrice(3.5);
      case columnTitle === '7 g' && this.capturesClassicSize(7, useLowerBoundStrategy):
        return calculateOuncesPrice(7);
      case columnTitle === '14 g' && this.capturesClassicSize(14, useLowerBoundStrategy):
        return calculateOuncesPrice(14);
      case columnTitle === '28 g' && this.capturesClassicSize(28, useLowerBoundStrategy):
        return calculateOuncesPrice(28);
      default:
        return null;
    }
  }

  private hasAnyMatchWithStrategy(isLowerBoundStrategy: boolean): boolean {
    return (
      this.capturesClassicSize(1, isLowerBoundStrategy) ||
      this.capturesClassicSize(3.5, isLowerBoundStrategy) ||
      this.capturesClassicSize(7, isLowerBoundStrategy) ||
      this.capturesClassicSize(14, isLowerBoundStrategy) ||
      this.capturesClassicSize(28, isLowerBoundStrategy)
    );
  }

  getUniqueIdentifier(): string {
    return `
      -${this.name}
      -${this.startWeight}
      -${this.startQuantity}
      -${this.endWeight}
      -${this.endQuantity}
      -${this.price}
      -${this.taxesInPrice}
      -${this.isDiscountable}
    `;
  }
}
